import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AUTH_BYPASS } from '@/core/auth/dev-bypass'
import { useAuthStore } from '@/core/auth/auth-store'
import { contactsRepository } from './api/contacts-repository'
import { playersPublicRepository } from '@/features/jugadores/api/players-public-repository'
import type { PlayerPublicDoc } from '@/core/models/player-public'
import type { ContactDoc } from '@/core/models/contact'

// `uid!` below is safe by the query's own gate: every one of these hooks sets
// `enabled: uid !== null` (or `!AUTH_BYPASS && uid !== null`), so TanStack
// Query never invokes the fn while uid is null. Asserting is preferable to a
// `?? ''` fallback, which would silently issue a query for the empty-string
// user and return an honest-looking empty result instead of not running.


// Real contacts + blocks (release item R2; data-model § contacts). The list
// joins each contact with its players_public projection for display — small
// N, and the projection is the one truthful public source (spec/06).

export interface ContactEntry {
  contact: ContactDoc
  profile: PlayerPublicDoc | null
}

export function useMyContacts() {
  const uid = useAuthStore((s) => s.user?.uid ?? null)
  return useQuery({
    queryKey: ['contacts', uid],
    queryFn: async (): Promise<ContactEntry[]> => {
      const docs = await contactsRepository.listMine(uid!)
      const profiles = await Promise.all(
        docs.map((c) => playersPublicRepository.getById(c.contactUid)),
      )
      return docs.map((contact, i) => ({ contact, profile: profiles[i] ?? null }))
    },
    enabled: !AUTH_BYPASS && uid !== null,
    staleTime: 30_000,
  })
}

/** Uids whose matches the feed must hide (status blocked). Memoized on the
 *  query data so it can sit in dependency arrays without thrashing. */
export function useBlockedUids(): Set<string> {
  const contacts = useMyContacts()
  return useMemo(
    () =>
      new Set(
        (contacts.data ?? [])
          .filter((e) => e.contact.status === 'blocked')
          .map((e) => e.contact.contactUid),
      ),
    [contacts.data],
  )
}

function useContactsInvalidate() {
  const qc = useQueryClient()
  return () => void qc.invalidateQueries({ queryKey: ['contacts'] })
}

export function useSaveContact() {
  const uid = useAuthStore((s) => s.user?.uid ?? null)
  const invalidate = useContactsInvalidate()
  // The contacts list is already loaded on every screen that offers "Guardar",
  // so the current status is in hand — pass it through, and `save` can refuse
  // to un-block without a read-then-write race. Without this the guard is
  // unreachable and saving a blocked player silently un-blocks them.
  const contacts = useMyContacts()
  return useMutation({
    mutationFn: (contactUid: string) =>
      contactsRepository.save(uid!, contactUid, {
        existingStatus:
          contacts.data?.find((e) => e.contact.contactUid === contactUid)?.contact.status ?? null,
      }),
    onSuccess: invalidate,
  })
}

export function useBlockPlayer() {
  const uid = useAuthStore((s) => s.user?.uid ?? null)
  const invalidate = useContactsInvalidate()
  return useMutation({
    mutationFn: (contactUid: string) => contactsRepository.block(uid!, contactUid),
    onSuccess: invalidate,
  })
}

export function useRemoveContact() {
  const uid = useAuthStore((s) => s.user?.uid ?? null)
  const invalidate = useContactsInvalidate()
  return useMutation({
    mutationFn: (contactUid: string) => contactsRepository.removeContact(uid!, contactUid),
    onSuccess: invalidate,
  })
}
